import React, { forwardRef, PropsWithChildren } from 'react';
import { css, styled } from 'styled-components';

export interface PanelProps extends PropsWithChildren {
  ref?: React.Ref<HTMLDivElement> | undefined;
  $grow?: number;
  $minHeight?: string;
  $height?: string;
  id?: string;
  $noAutoHeight?: boolean;
  $padding?: React.CSSProperties['padding'];
  $paddingTop?: React.CSSProperties['paddingTop'];
  $paddingRight?: React.CSSProperties['paddingRight'];
  $paddingBottom?: React.CSSProperties['paddingBottom'];
  $paddingLeft?: React.CSSProperties['paddingLeft'];
  $margin?: React.CSSProperties['margin'];
  $marginTop?: React.CSSProperties['marginTop'];
  $marginRight?: React.CSSProperties['marginRight'];
  $marginBottom?: React.CSSProperties['marginBottom'];
  $marginLeft?: React.CSSProperties['marginLeft'];
}

const PanelWrapper = styled.div<PanelProps>`
  display: flex;
  flex-direction: row;
  flex-grow: ${props => props.$grow ?? 1};
  ${props =>
    props.$padding &&
    css`
      padding: ${props.$padding};
    `}
  ${props =>
    props.$paddingTop &&
    css`
      padding-top: ${props.$paddingTop};
    `}
  ${props =>
    props.$paddingRight &&
    css`
      padding-right: ${props.$paddingRight};
    `}
  ${props =>
    props.$paddingBottom &&
    css`
      padding-bottom: ${props.$paddingBottom};
    `}
  ${props =>
    props.$paddingLeft &&
    css`
      padding-left: ${props.$paddingLeft};
    `}
  ${props =>
    props.$margin &&
    css`
      margin: ${props.$margin};
    `}
  ${props =>
    props.$marginTop &&
    css`
      margin-top: ${props.$marginTop};
    `}
  ${props =>
    props.$marginRight &&
    css`
      margin-right: ${props.$marginRight};
    `}
  ${props =>
    props.$marginBottom &&
    css`
      margin-bottom: ${props.$marginBottom};
    `}
  ${props =>
    props.$marginLeft &&
    css`
      margin-left: ${props.$marginLeft};
    `}
  ${props => {
    if (props.$minHeight) {
      return css`
        min-height: ${props.$minHeight};
      `;
    } else {
      if (!props.$noAutoHeight) {
        return css`
          min-height: 100%;
        `;
      }
    }
  }}
  ${props => {
    if (props.$height) {
      return css`
        height: ${props.$height};
      `;
    } else {
      if (!props.$noAutoHeight) {
        return css`
          height: 100%;
        `;
      }
    }
  }}

  align-items: flex-start;
`;

/**
 * Non-visual component that can be used to create a flex panel with row direction
 * and maximum available height if not specified by the ```height``` attribute or
 * suppressed by the ```noAutoHeight``` attribute.
 */
export const Panel: React.FC<PanelProps> = forwardRef(
  (
    {
      $grow,
      children,
      $height,
      $minHeight,
      $padding,
      $paddingTop,
      $paddingRight,
      $paddingBottom,
      $paddingLeft,
      $margin,
      $marginTop,
      $marginRight,
      $marginBottom,
      $marginLeft,
      ...rest
    },
    ref,
  ) => {
    return (
      <PanelWrapper
        $grow={$grow ?? 0}
        ref={ref}
        $height={$height}
        $minHeight={$minHeight}
        $padding={$padding}
        $paddingTop={$paddingTop}
        $paddingRight={$paddingRight}
        $paddingBottom={$paddingBottom}
        $paddingLeft={$paddingLeft}
        $margin={$margin}
        $marginTop={$marginTop}
        $marginRight={$marginRight}
        $marginBottom={$marginBottom}
        $marginLeft={$marginLeft}
        {...rest}
        data-type="Panel"
      >
        {children}
      </PanelWrapper>
    );
  },
);
Panel.displayName = 'Panel';
